<template>
  <div class="help-text-tooltip d-inline-block">
    <i 
      class="material-icons cursor-pointer text-info help-icon" 
      @click="toggleHelp"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      :title="title"
    >
      help_outline
    </i>
    
    <!-- Popover Content -->
    <transition name="fade">
      <div 
        v-if="showHelp && helpContent" 
        class="help-popover"
        :class="{ 'help-popover-hover': mode === 'hover' }"
      >
        <div class="help-popover-header">
          <h6 class="mb-0">{{ helpContent.title }}</h6>
          <i 
            v-if="mode === 'click'" 
            class="material-icons cursor-pointer close-icon"
            @click="showHelp = false"
          >
            close
          </i>
        </div>
        <div class="help-popover-body" v-html="helpContent.body"></div>
        <div v-if="helpContent.objectdesc" class="help-popover-footer">
          <small class="text-muted">{{ helpContent.objectdesc }}</small>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  helpKey: {
    type: String,
    required: true,
    default: ''
  },
  mode: {
    type: String,
    default: 'click', // 'click' or 'hover'
    validator: (value) => ['click', 'hover'].includes(value)
  },
  title: {
    type: String,
    default: 'Click for help'
  }
});

const store = useStore();
const showHelp = ref(false);
let hoverTimeout = null;

const helpContent = computed(() => 
  store.getters.getHelpTextByKey(props.helpKey)
);

const toggleHelp = () => {
  if (props.mode === 'click') {
    showHelp.value = !showHelp.value;
  }
};

const onMouseEnter = () => {
  if (props.mode === 'hover') {
    clearTimeout(hoverTimeout);
    hoverTimeout = setTimeout(() => {
      showHelp.value = true;
    }, 300); // Small delay before showing
  }
};

const onMouseLeave = () => {
  if (props.mode === 'hover') {
    clearTimeout(hoverTimeout);
    hoverTimeout = setTimeout(() => {
      showHelp.value = false;
    }, 200);
  }
};

onMounted(async () => {
  // Load help text when unavailable, then refresh stale cached data if this key is missing.
  try {
    if (!store.getters.getHelpTextByKey(props.helpKey)) {
      if (Object.keys(store.getters.stHelpTextData || {}).length === 0) {
        await store.dispatch('fetchHelpText');
      }
      if (!store.getters.getHelpTextByKey(props.helpKey)) {
        await store.dispatch('fetchHelpText', { forceRefresh: true });
      }
    }
  } catch (error) {
    console.error('Failed to fetch help text:', error);
  }
});
</script>

<style scoped>
.help-text-tooltip {
  position: relative;
  vertical-align: middle;
}

.help-icon {
  font-size: 18px;
  transition: color 0.2s ease;
}

.help-icon:hover {
  color: #0d6efd !important;
}

.help-popover {
  position: absolute;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1050;
  min-width: 280px;
  max-width: 400px;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 8px;
}

/* If popover would go off-screen, adjust position */
.help-popover-hover {
  left: auto;
  right: 100%;
  margin-left: 0;
  margin-right: 8px;
}

.help-popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
  border-radius: 6px 6px 0 0;
}

.help-popover-header h6 {
  color: #0d6efd;
  font-weight: 600;
  font-size: 14px;
}

.close-icon {
  font-size: 18px;
  color: #6c757d;
  transition: color 0.2s ease;
}

.close-icon:hover {
  color: #dc3545;
}

.help-popover-body {
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.6;
  color: #495057;
  max-height: 300px;
  overflow-y: auto;
}

.help-popover-body :deep(a) {
  color: #0d6efd;
  text-decoration: none;
}

.help-popover-body :deep(a:hover) {
  text-decoration: underline;
}

.help-popover-footer {
  padding: 8px 16px;
  border-top: 1px solid #dee2e6;
  background-color: #f8f9fa;
  border-radius: 0 0 6px 6px;
}

.help-popover-footer small {
  font-size: 11px;
  font-style: italic;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Scrollbar styling for help content */
.help-popover-body::-webkit-scrollbar {
  width: 6px;
}

.help-popover-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.help-popover-body::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.help-popover-body::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.cursor-pointer {
  cursor: pointer;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .help-popover {
    left: 50%;
    transform: translateX(-50%);
    top: 100%;
    margin-top: 8px;
    margin-left: 0;
  }
  
  .help-popover-hover {
    left: 50%;
    right: auto;
    margin-right: 0;
  }
}
</style>

